/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { UapiMcpCore } from "../core.js";
import { appendForm } from "../lib/encodings.js";
import {
  getContentTypeFromFileName,
  readableStreamToArrayBuffer,
} from "../lib/files.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import { APIError } from "../models/errors/apierror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import {
  PostImageNsfwRequest,
  PostImageNsfwRequest$zodSchema,
} from "../models/postimagensfwop.js";
import { APICall, APIPromise } from "../types/async.js";
import { isBlobLike } from "../types/blobs.js";
import { Result } from "../types/fp.js";
import { isReadableStream } from "../types/streams.js";

/**
 * 图片敏感检测
 *
 * @remarks
 * 这是一个图片内容审核接口，自动识别图片中的违规内容并返回处理建议。
 *
 * > [!VIP]
 * > 此接口限时免费开放，无需企业认证即可使用。
 *
 * ## 功能概述
 * 上传图片文件或提供图片URL，接口会自动分析图片内容，返回是否违规、风险等级和处理建议。适合对接到用户上传流程中，实现自动化内容审核。
 *
 * ## 返回字段说明
 * - **is_nsfw**: 是否判定为违规内容，`true` 表示违规，`false` 表示正常
 * - **nsfw_score**: 违规内容置信度，0-1 之间，越高表示越可能违规
 * - **normal_score**: 正常内容置信度，0-1 之间，与 nsfw_score 互补
 * - **suggestion**: 处理建议
 *   - `pass`: 内容正常，可以直接放行
 *   - `review`: 存在风险，建议转人工复核
 *   - `block`: 高风险内容，建议直接拦截
 * - **risk_level**: 风险等级
 *   - `low`: 低风险
 *   - `medium`: 中风险
 *   - `high`: 高风险
 * - **label**: 内容标签，`nsfw` 或 `normal`
 * - **confidence**: 模型对当前判断的整体置信度
 * - **inference_time_ms**: 模型推理耗时，单位毫秒
 */
export function imagePostImageNsfw(
  client$: UapiMcpCore,
  request: PostImageNsfwRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    Response,
    | APIError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
> {
  return new APIPromise($do(
    client$,
    request,
    options,
  ));
}

async function $do(
  client$: UapiMcpCore,
  request: PostImageNsfwRequest,
  options?: RequestOptions,
): Promise<
  [
    Result<
      Response,
      | APIError
      | SDKValidationError
      | UnexpectedClientError
      | InvalidRequestError
      | RequestAbortedError
      | RequestTimeoutError
      | ConnectionError
    >,
    APICall,
  ]
> {
  const parsed$ = safeParse(
    request,
    (value$) => PostImageNsfwRequest$zodSchema.parse(value$),
    "Input validation failed",
  );
  if (!parsed$.ok) {
    return [parsed$, { status: "invalid" }];
  }
  const payload$ = parsed$.value;
  const body$ = new FormData();
  if (payload$.file !== undefined) {
    if (isBlobLike(payload$.file)) {
      appendForm(body$, "file", payload$.file);
    } else if (isReadableStream(payload$.file.content)) {
      const buffer = await readableStreamToArrayBuffer(payload$.file.content);
      const contentType = getContentTypeFromFileName(payload$.file.fileName)
        || "application/octet-stream";
      const blob = new Blob([buffer], { type: contentType });
      appendForm(body$, "file", blob, payload$.file.fileName);
    } else {
      const contentType = getContentTypeFromFileName(payload$.file.fileName)
        || "application/octet-stream";
      appendForm(
        body$,
        "file",
        new Blob([payload$.file.content], { type: contentType }),
        payload$.file.fileName,
      );
    }
  }
  if (payload$.url !== undefined) {
    appendForm(body$, "url", payload$.url);
  }
  const path$ = pathToFunc("/image/nsfw")();

  const headers$ = new Headers(compactMap({
    Accept: "application/json",
  }));
  const securityInput = await extractSecurity(client$._options.security);
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client$._options,
    baseURL: options?.serverURL ?? client$._baseURL ?? "",
    operationID: "post-image-nsfw",
    oAuth2Scopes: null,
    resolvedSecurity: requestSecurity,
    securitySource: client$._options.security,
    retryConfig: options?.retries
      || client$._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || [
      "429",
      "500",
      "502",
      "503",
      "504",
    ],
  };

  const requestRes = client$._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path$,
    headers: headers$,
    body: body$,
    userAgent: client$._options.userAgent,
    timeoutMs: options?.timeoutMs || client$._options.timeoutMs
      || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req$ = requestRes.value;

  const doResult = await client$._do(req$, {
    context,
    errorCodes: [],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req$ }];
  }
  return [doResult, {
    status: "complete",
    "request": req$,
    response: doResult.value,
  }];
}
